import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
} from "react";
import { sessionEvents } from "../events/sessionEvents";
import { setOpenModalCallback } from "../api/patientRegistry";
import { authzStore } from "../authz/authzStore";
import { jwtStore } from "./jwtStore";

interface AuthContextValue {
    /** The current cached JWT, or null if none is set. */
    jwt: string | null;
    /** Whether the JWT-paste modal is currently shown. */
    isModalOpen: boolean;
    /** Force the modal open (used when an API call needs auth). */
    openModal: () => void;
    /** Close the modal without changing JWT state. */
    closeModal: () => void;
    /** Clear the cached JWT and re-prompt on next protected action. */
    logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const jwt = useSyncExternalStore(
        jwtStore.subscribe,
        jwtStore.get,
        () => null,
    );

    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const logout = useCallback(() => {
        jwtStore.clear();
        // PRD-005: re-pasting a fresh token (post-permission-grant) needs the
        // deny flag cleared so the gate stack flips back to the happy path.
        authzStore.clear();
        sessionEvents.emit("auth:logout");
        setIsModalOpen(true);
    }, []);

    // Wire the fetch wrapper's "open paste modal" callback to this provider.
    useEffect(() => {
        setOpenModalCallback(openModal);
        return () => setOpenModalCallback(() => {});
    }, [openModal]);

    // PRD-002 §JWT-acquisition DoD: on initial page load (and any subsequent
    // transition into the no-JWT state, e.g. via logout or a 401-driven
    // clear), surface the paste modal automatically — without waiting for
    // a protected action. The modal stays dismissible; deps are `[jwt]` so
    // a user-initiated close doesn't immediately re-open. The fetch
    // chokepoint at `patientRegistryFetch` still re-opens on the first
    // 401 path; this effect just removes the "land on the bulk-export
    // form, hunt for the Authorize button" friction the user reported.
    useEffect(() => {
        if (jwt === null) setIsModalOpen(true);
    }, [jwt]);

    const value = useMemo(
        () => ({ jwt, isModalOpen, openModal, closeModal, logout }),
        [jwt, isModalOpen, openModal, closeModal, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
    return ctx;
}
